<template>
  <div>
    <!-- Prove-based -->
    <div
      v-if="config.type === 'prove-based'"
      class="grid grid-cols-2 gap-3">
      <button
        type="button"
        :disabled="isDisabled"
        :class="[
          'cursor-pointer rounded-lg border px-4 py-3 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50',
          provedValue === false
            ? 'border-action bg-surface-muted text-text-primary'
            : 'border-border-subtle bg-surface text-text-primary hover:border-border-strong',
        ]"
        @click="setProved(false)">
        {{ config.notProvedLabel }}
      </button>
      <button
        type="button"
        :disabled="isDisabled"
        :class="[
          'cursor-pointer rounded-lg border px-4 py-3 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50',
          provedValue === true
            ? 'border-action bg-surface-muted text-text-primary'
            : 'border-border-subtle bg-surface text-text-primary hover:border-border-strong',
        ]"
        @click="setProved(true)">
        {{ config.provedLabel }}
      </button>
      <InfoText
        v-if="showQaWarning"
        theme="warning"
        :text="$t('W: QA påkrævet')"
        class="col-span-2 w-fit" />
    </div>

    <!-- Value-based -->
    <div
      v-else-if="config.type === 'value-based'"
      class="flex flex-col gap-2">
      <div>
        <label class="mb-1 block text-xs font-medium text-text-secondary">
          {{ $t('W: Målt værdi') }}
          <span
            v-if="unitText"
            class="font-normal text-text-muted">
            ({{ unitText }})
          </span>
        </label>
        <input
          type="number"
          step="any"
          :disabled="isDisabled"
          class="w-full rounded-lg border border-border-subtle bg-surface px-3 py-2 text-sm focus:border-action focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
          :class="{
            'border-action bg-surface-muted':
              enteredValue !== null && specialValue == null,
          }"
          v-number-value="specialValue != null ? null : enteredValue"
          :placeholder="$t('W: Angiv måling...')"
          @input="onValueInput" />
      </div>
      <button
        v-if="config.hasTooLowToMeasure"
        type="button"
        :disabled="isDisabled"
        :class="[
          'cursor-pointer rounded-lg border px-4 py-3 text-left text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50',
          specialValue === 'too-low'
            ? 'border-action bg-surface-muted text-text-primary'
            : 'border-border-subtle bg-surface text-text-primary hover:border-border-strong',
        ]"
        @click="onSpecialValueClick('too-low')">
        {{ $t('W: For lavt til at måle')
        }}{{ getLimitText('min') ? ` (${getLimitText('min')})` : '' }}
      </button>
      <button
        v-if="config.hasTooHighToMeasure"
        type="button"
        :disabled="isDisabled"
        :class="[
          'cursor-pointer rounded-lg border px-4 py-3 text-left text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50',
          specialValue === 'too-high'
            ? 'border-action bg-surface-muted text-text-primary'
            : 'border-border-subtle bg-surface text-text-primary hover:border-border-strong',
        ]"
        @click="onSpecialValueClick('too-high')">
        {{ $t('W: For højt til at måle')
        }}{{ getLimitText('max') ? ` (${getLimitText('max')})` : '' }}
      </button>
    </div>

    <!-- Single-select -->
    <div
      v-else-if="config.type === 'single-select'"
      class="flex flex-col gap-2">
      <button
        v-for="answer in answersFor(selectedAnswerID)"
        :key="answer.id"
        type="button"
        :disabled="isDisabled"
        :class="[
          'cursor-pointer rounded-lg border px-4 py-3 text-left text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50',
          selectedAnswerID === answer.id
            ? 'border-action bg-surface-muted text-text-primary'
            : 'border-border-subtle bg-surface text-text-primary hover:border-border-strong',
        ]"
        @click="selectAnswer(answer.id)">
        {{ answerLabel(answer.text) }}
      </button>
      <InfoText
        v-if="showQaWarning"
        theme="warning"
        :text="$t('W: QA påkrævet')"
        class="w-fit" />
    </div>

    <!-- Multi-select text -->
    <div
      v-else-if="config.type === 'multi-select-text'"
      class="flex flex-col gap-3">
      <!-- The rows are dragged into the order the report prints them in. The handle is the same
           grip as the Bilag list's (UiEditableList); a number test has none, its rows are sorted
           on the report. -->
      <draggable
        v-if="multiSelectTextSelectedIDs.length > 0"
        :modelValue="multiSelectTextRows"
        itemKey="key"
        handle=".result-row-drag-handle"
        :animation="150"
        :disabled="!canReorderMultiSelectTextRows"
        tag="div"
        class="flex flex-col gap-2"
        @update:model-value="onMultiSelectTextReorder">
        <template #item="{ element: { item }, index }">
          <div
            class="flex items-center gap-2 rounded-lg border border-border-subtle bg-surface-muted px-3 py-2">
          <span
            v-if="canReorderMultiSelectTextRows"
            class="result-row-drag-handle flex shrink-0 cursor-grab text-text-muted active:cursor-grabbing">
            <IconLucideGripVertical class="h-4 w-4" />
          </span>
          <!-- The remove button sits beside both rows, centred on the whole card. -->
          <div class="flex min-w-0 flex-1 flex-col gap-2">
            <!-- Row 1: the name and its suffix. -->
            <div class="flex min-w-0 flex-col">
              <span class="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                <span class="text-sm font-medium text-text-primary">
                  {{
                    getReportDescriptionItemLabel(item.reportDescriptionItemID)
                  }}
                </span>
                <select
                  v-if="showsSuffixSelect(item)"
                  :disabled="isDisabled"
                  class="cursor-pointer field-sizing-content rounded border border-border-subtle bg-surface px-2 py-1 text-sm text-text-primary focus:border-action focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                  :class="{ 'border-action': !!item.suffixID }"
                  :value="item.suffixID ?? ''"
                  @change="onMultiSelectTextSuffixChange(index, $event)">
                  <option value="">{{ $t('W: Intet suffiks') }}</option>
                  <option
                    v-for="suffix in suffixOptionsFor(item.suffixID)"
                    :key="suffix.value"
                    :value="suffix.value">
                    {{ suffix.text }}
                  </option>
                </select>
              </span>
              <span
                v-if="
                  getReportDescriptionItemSourceLabel(
                    item.reportDescriptionItemID
                  )
                "
                class="text-xs text-text-muted">
                {{
                  getReportDescriptionItemSourceLabel(
                    item.reportDescriptionItemID
                  )
                }}
              </span>
            </div>
            <!-- Row 2: the fields for this row, always below the name, whether or not there is a suffix. -->
            <div
              v-if="config.answerMode !== 'none'"
              class="flex flex-wrap items-center gap-2">
              <select
                :disabled="isDisabled"
                class="cursor-pointer rounded border border-border-subtle bg-surface px-2 py-1 text-sm text-text-primary focus:border-action focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                :class="{ 'border-action': !!item.answerID }"
                :value="item.answerID ?? ''"
                @change="onMultiSelectTextAnswerChange(index, $event)">
                <option value="">{{ $t('W: Vælg resultat...') }}</option>
                <option
                  v-for="answer in answersFor(item.answerID)"
                  :key="answer.id"
                  :value="answer.id">
                  {{ answerLabel(answer.text) }}
                </option>
              </select>
            </div>
          </div>
          <!-- Same square hit area and hover as a table row action (DynamicTable's IconWrap). -->
          <button
            type="button"
            :disabled="isDisabled"
            :aria-label="$t('W: Fjern')"
            class="-my-1 -mr-1 flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-md text-text-primary transition-colors hover:bg-surface-sunken focus-visible:ring-2 focus-visible:ring-action focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
            @click="removeMultiSelectTextItem(index)">
            <IconLucideX class="h-5 w-5" />
          </button>
          </div>
        </template>
      </draggable>

      <UiComboboxVee
        name="multi-select-text-add"
        :options="
          reportDescriptionItemComboboxOptions(multiSelectTextSelectedIDs)
        "
        :modelValue="''"
        :locale="locale"
        :placeholder="$t('W: Søg og tilføj...')"
        :isDisabled="isDisabled || multiSelectTextNothingToAdd"
        :allowCreate="canCreateReportDescriptionItemOnTheFly"
        class="mb-0!"
        @update:model-value="onMultiSelectTextComboboxSelect"
        @on-pending-create="onMultiSelectTextPendingCreate" />

      <button
        v-if="multiSelectTextSelectedIDs.length === 0"
        type="button"
        :disabled="isDisabled"
        :class="[
          'flex w-full cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50',
          multiSelectTextNothingToAdd
            ? 'border-action bg-surface-muted text-text-primary'
            : 'border-border-subtle bg-surface text-text-primary hover:border-border-strong',
        ]"
        @click="toggleMultiSelectTextNothingToAdd">
        <IconLucideCheckSquare
          v-if="multiSelectTextNothingToAdd"
          class="h-4 w-4 shrink-0" />
        <IconLucideSquare
          v-else
          class="h-4 w-4 shrink-0" />
        {{ $t('W: Intet at tilføje') }}
      </button>
    </div>

    <!-- Multi-select number -->
    <div
      v-else-if="config.type === 'multi-select-number'"
      class="flex flex-col gap-3">
      <div
        v-if="multiSelectNumberSelectedIDs.length > 0"
        class="flex flex-col gap-2">
        <div
          v-for="(item, index) in multiSelectNumberItems"
          :key="index"
          class="flex items-center gap-2 rounded-lg border border-border-subtle bg-surface-muted px-3 py-2">
          <!-- The remove button sits beside both rows, centred on the whole card. -->
          <div class="flex min-w-0 flex-1 flex-col gap-2">
            <!-- Row 1: the name and its suffix. -->
            <div class="flex min-w-0 flex-col">
              <span class="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                <span class="text-sm font-medium text-text-primary">
                  {{
                    getReportDescriptionItemLabel(item.reportDescriptionItemID)
                  }}
                </span>
                <select
                  v-if="showsSuffixSelect(item)"
                  :disabled="isDisabled"
                  class="cursor-pointer field-sizing-content rounded border border-border-subtle bg-surface px-2 py-1 text-sm text-text-primary focus:border-action focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                  :class="{ 'border-action': !!item.suffixID }"
                  :value="item.suffixID ?? ''"
                  @change="onMultiSelectNumberSuffixChange(index, $event)">
                  <option value="">{{ $t('W: Intet suffiks') }}</option>
                  <option
                    v-for="suffix in suffixOptionsFor(item.suffixID)"
                    :key="suffix.value"
                    :value="suffix.value">
                    {{ suffix.text }}
                  </option>
                </select>
              </span>
              <span
                v-if="
                  getReportDescriptionItemSourceLabel(
                    item.reportDescriptionItemID
                  )
                "
                class="text-xs text-text-muted">
                {{
                  getReportDescriptionItemSourceLabel(
                    item.reportDescriptionItemID
                  )
                }}
              </span>
            </div>
            <!-- Row 2: the fields for this row, always below the name, whether or not there is a suffix. -->
            <div class="flex flex-wrap items-center gap-2">
              <select
                v-if="answerOverridesFor(item.answerOverrideID).length > 0"
                :disabled="isDisabled"
                class="cursor-pointer rounded border border-border-subtle bg-surface px-2 py-1 text-sm text-text-primary focus:border-action focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                :class="{ 'border-action': item.answerOverrideID != null }"
                :value="item.answerOverrideID ?? ''"
                @change="onMultiSelectNumberOverrideChange(index, $event)">
                <option value="">{{ $t('W: Angiv tal') }}</option>
                <option
                  v-for="override in answerOverridesFor(item.answerOverrideID)"
                  :key="override.id"
                  :value="override.id">
                  {{ override.text }}
                </option>
              </select>
              <input
                type="number"
                step="any"
                :disabled="isDisabled || item.answerOverrideID != null"
                class="w-24 rounded border border-border-subtle bg-surface px-2 py-1 text-sm text-text-primary focus:border-action focus:outline-none disabled:cursor-not-allowed disabled:opacity-40"
                :class="{
                  'border-action': item.value != null,
                  invisible: item.answerOverrideID != null,
                }"
                v-number-value="item.value ?? null"
                :placeholder="$t('W: Antal...')"
                @input="onMultiSelectNumberValueInput(index, $event)" />
              <span
                v-if="unitText"
                :class="[
                  'text-xs whitespace-nowrap text-text-muted',
                  { invisible: item.answerOverrideID != null },
                ]">
                {{ unitText }}
              </span>
            </div>
          </div>
          <!-- Same square hit area and hover as a table row action (DynamicTable's IconWrap). -->
          <button
            type="button"
            :disabled="isDisabled"
            :aria-label="$t('W: Fjern')"
            class="-my-1 -mr-1 flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-md text-text-primary transition-colors hover:bg-surface-sunken focus-visible:ring-2 focus-visible:ring-action focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
            @click="removeMultiSelectNumberItem(index)">
            <IconLucideX class="h-5 w-5" />
          </button>
        </div>
      </div>

      <UiComboboxVee
        name="multi-select-number-add"
        :options="
          reportDescriptionItemComboboxOptions(multiSelectNumberSelectedIDs)
        "
        :modelValue="''"
        :locale="locale"
        :placeholder="$t('W: Søg og tilføj...')"
        :isDisabled="isDisabled || multiSelectNumberNothingToAdd"
        :allowCreate="canCreateReportDescriptionItemOnTheFly"
        class="mb-0!"
        @update:model-value="onMultiSelectNumberComboboxSelect"
        @on-pending-create="onMultiSelectNumberPendingCreate" />

      <div
        v-if="multiSelectNumberSelectedIDs.length > 0 && config.hasSumValue"
        class="flex flex-col gap-1">
        <label class="mb-1 block text-xs font-medium text-text-secondary">
          {{ $t('W: Sum') }}
        </label>
        <input
          type="text"
          maxlength="250"
          :disabled="isDisabled"
          class="w-full rounded-lg border border-border-subtle bg-surface px-3 py-2 text-sm focus:border-action focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
          :value="multiSelectNumberSumValue"
          :placeholder="$t('W: Angiv sum...')"
          @input="onMultiSelectNumberSumInput" />
        <p
          v-if="multiSelectNumberSumHint !== null"
          class="text-xs text-text-muted">
          {{ $t('W: Beregnet sum') }}: {{ multiSelectNumberSumHint }}
        </p>
      </div>

      <button
        v-if="multiSelectNumberSelectedIDs.length === 0"
        type="button"
        :disabled="isDisabled"
        :class="[
          'flex w-full cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50',
          multiSelectNumberNothingToAdd
            ? 'border-action bg-surface-muted text-text-primary'
            : 'border-border-subtle bg-surface text-text-primary hover:border-border-strong',
        ]"
        @click="toggleMultiSelectNumberNothingToAdd">
        <IconLucideCheckSquare
          v-if="multiSelectNumberNothingToAdd"
          class="h-4 w-4 shrink-0" />
        <IconLucideSquare
          v-else
          class="h-4 w-4 shrink-0" />
        {{ $t('W: Intet at tilføje') }}
      </button>
    </div>

    <!-- Not yet supported -->
    <p
      v-else
      class="text-sm text-text-muted italic">
      {{ $t('W: Ikke understøttet endnu') }}
    </p>
  </div>
</template>

<script lang="ts">
import { mapState } from 'pinia'
import draggable from 'vuedraggable'
import { vNumberValue } from '@/globals/typescript/directives/numberValue'
import type { PropType } from 'vue'
import { useAppStore } from '@/stores/appStore'
import { useTextListStore } from '@/stores/textListStore'
import { useReportDescriptionStore } from '@/stores/reportDescriptionStore'
import { useReportDescriptionItemStore } from '@/stores/reportDescriptionItemStore'
import { generateFirestoreId } from '@/globals/typescript/utils/utils'
import { getTextObjValue } from '@team-milva/lab-models/textObj'
import type { ITextObj } from '@team-milva/lab-models/textObj'
import type { TAnalysisResultFieldsConfig } from '@/globals/typescript/utils/analysisResultFieldsConfig'
import type {
  TAnalysisResultSpecialValue,
  TAnalysisResultValue,
} from '@team-milva/lab-models/resources'

type TPendingReportDescriptionItem = {
  id: string
  reportDescriptionID: string
  text: string
  locale: string
}

type TMultiSelectTextItem = {
  reportDescriptionItemID: string
  answerID: string | null
  suffixID?: string | null
}

// A multi-select-text row as the drag list sees it: the item plus a key that follows it as it moves.
type TMultiSelectTextRow = { key: string; item: TMultiSelectTextItem }

// A key for a drag-list row. Not an id of anything stored, so a counter rather than a Firestore id.
let rowKeyCounter = 0
const nextRowKey = (): string => `row-${++rowKeyCounter}`

// Sum of the numeric answers, leaving out rows that carry an override answer.
// Rounded so decimals typed live do not show float noise ("0.1 + 0.2" → 0.3).
function sumNumericAnswers(
  items: { value: number | null; answerOverrideID: string | null }[]
): number {
  const sum = items.reduce((acc, item) => {
    if (item.answerOverrideID != null) return acc
    return acc + (item.value ?? 0)
  }, 0)
  return Number(sum.toFixed(10))
}

// Positions where `token` appears as a whole number in `text`: not glued to another
// digit, and not the integer or decimal part of a longer number ("5" is not in "15",
// "5,5" or "1,5").
function findStandaloneNumber(text: string, token: string): number[] {
  const isDigit = (c: string | undefined) => c !== undefined && c >= '0' && c <= '9'
  const isSeparator = (c: string | undefined) => c === '.' || c === ','
  const hits: number[] = []
  let from = 0
  for (;;) {
    const at = text.indexOf(token, from)
    if (at === -1) break
    const end = at + token.length
    const isPartOfLargerNumber =
      isDigit(text[at - 1]) ||
      (isSeparator(text[at - 1]) && isDigit(text[at - 2])) ||
      isDigit(text[end]) ||
      (isSeparator(text[end]) && isDigit(text[end + 1]))
    if (!isPartOfLargerNumber) hits.push(at)
    from = at + 1
  }
  return hits
}

// Swaps the old computed sum for the new one inside text the user has edited
// ("ca. 15 CFU/m³ (estimat)" → "ca. 17 CFU/m³ (estimat)"), keeping the number's own
// decimal separator. Only when the old sum appears exactly once — with none, or with
// several candidates, nothing is touched. Returns null in those cases.
function replaceNumberInSum(
  text: string,
  previousSum: number,
  nextSum: number
): string | null {
  const dot = String(previousSum)
  const comma = dot.replace('.', ',')
  const candidates = [
    ...findStandaloneNumber(text, dot).map((at) => ({ at, token: dot, isComma: false })),
    ...(comma !== dot
      ? findStandaloneNumber(text, comma).map((at) => ({ at, token: comma, isComma: true }))
      : []),
  ]
  const candidate = candidates[0]
  if (candidates.length !== 1 || !candidate) return null
  const { at, token, isComma } = candidate
  const replacement = isComma ? String(nextSum).replace('.', ',') : String(nextSum)
  return text.slice(0, at) + replacement + text.slice(at + token.length)
}

export default {
  name: 'AnalysisResultFields',

  components: { draggable },

  // Not `:value` on the number inputs: that refills the field after every keystroke and drops
  // typed digits (0,05 became 5). See the directive.
  directives: { numberValue: vNumberValue },

  emits: ['update:modelValue', 'on-pending-report-description-item-create'],

  props: {
    config: {
      type: Object as PropType<TAnalysisResultFieldsConfig>,
      required: true,
    },
    modelValue: {
      type: Object as PropType<TAnalysisResultValue | null>,
      default: null,
    },
    reportLocale: { type: String, default: '' },
    showQaWarning: { type: Boolean, default: false },
    isDisabled: { type: Boolean, default: false },
    pendingReportDescriptionItems: {
      type: Array as PropType<TPendingReportDescriptionItem[]>,
      default: () => [],
    },
    // Only ever non-empty in the analysis-test editor's preview pane, where a
    // unit can be created on the fly and is not in the store until save.
    pendingUnits: {
      type: Array as PropType<{ id: string; text: string }[]>,
      default: () => [],
    },
  },

  data() {
    return {
      // One key per multi-select-text row, in row order, for the drag list only — never saved.
      // A row has no id of its own and the same item can sit on the result twice, so the item id
      // cannot key it; a row keeps its key as it is dragged, and a position-based key would not.
      rowKeys: [] as string[],
    }
  },

  computed: {
    ...mapState(useAppStore, ['currentLocale']),
    ...mapState(useTextListStore, ['systemLists']),
    ...mapState(useReportDescriptionStore, ['allReportDescriptions']),
    ...mapState(useReportDescriptionItemStore, ['allReportDescriptionItems']),

    locale(): string {
      return this.reportLocale || this.currentLocale
    },

    provedValue(): boolean | undefined {
      return this.modelValue?.type === 'prove-based'
        ? (this.modelValue.isProved ?? undefined)
        : undefined
    },

    enteredValue(): number | null {
      return this.modelValue?.type === 'value-based'
        ? this.modelValue.value
        : null
    },

    specialValue(): TAnalysisResultSpecialValue | null {
      return this.modelValue?.type === 'value-based'
        ? this.modelValue.specialValue
        : null
    },

    selectedAnswerID(): string | null {
      return this.modelValue?.type === 'single-select'
        ? this.modelValue.answerID
        : null
    },

    // A test can pool items from up to 5 Collections at once — see
    // AnalysisTest.reportDescriptionIDs.
    reportDescriptionIDs(): string[] {
      return this.config.type === 'multi-select-text' ||
        this.config.type === 'multi-select-number'
        ? this.config.reportDescriptionIDs
        : []
    },

    suffixReportDescriptionIDs(): string[] {
      return this.config.type === 'multi-select-text' ||
        this.config.type === 'multi-select-number'
        ? this.config.suffixReportDescriptionIDs
        : []
    },

    // A test with no suffix Collections configured renders no suffix control at
    // all — the feature is opt-in per test (MIL-395).
    // The rows are rendered from the items array itself, and keyed on its
    // index rather than on reportDescriptionItemID. The de-duplication below
    // means an item id is unique today, but keying on it would bake that in:
    // allowing the same subject twice with different suffixes later would then
    // mean rewriting every handler here, instead of dropping one guard
    // (MIL-395). Do not "simplify" these back to id lookups.
    multiSelectTextItems(): {
      reportDescriptionItemID: string
      answerID: string | null
      suffixID?: string | null
    }[] {
      return this.modelValue?.type === 'multi-select-text'
        ? this.modelValue.items
        : []
    },

    multiSelectNumberItems(): {
      reportDescriptionItemID: string
      value: number | null
      answerOverrideID: string | null
      suffixID?: string | null
    }[] {
      return this.modelValue?.type === 'multi-select-number'
        ? this.modelValue.items
        : []
    },

    // Creating a new item on the fly needs exactly one target Collection to
    // write it into. With a single pooled Collection that's unambiguous; with
    // more than one, there's no UI to choose which one a new item belongs to,
    // so on-the-fly creation is disabled entirely rather than guessing.
    canCreateReportDescriptionItemOnTheFly(): boolean {
      return this.reportDescriptionIDs.length === 1
    },

    // Whether an item's source Collection is worth naming at all. With a
    // single pooled Collection it would just repeat the same name on every
    // row, so both the combobox and the selected rows below stay silent.
    showsCollectionSource(): boolean {
      return this.reportDescriptionIDs.length > 1
    },

    // Pools items across all of the test's referenced Collections into one
    // combined, searchable set — each item keeps track of which Collection it
    // came from, so the combobox can show that as a second line when a test
    // pools from more than one.
    pooledReportDescriptionItems(): {
      id: string
      title: Record<string, string>
      activeState: 'active' | 'inactive' | 'archived'
      createdAt: number
      collectionName: string
    }[] {
      if (this.reportDescriptionIDs.length === 0) return []
      const idSet = new Set(this.reportDescriptionIDs)
      const collectionNameByID = new Map(
        this.allReportDescriptions.map((rd) => [rd.id, rd.getName(this.locale)])
      )
      const storeItems = this.allReportDescriptionItems
        .filter((x) => idSet.has(x.reportDescriptionID))
        .map((x) => ({
          id: x.id,
          title: x.title,
          activeState: x.activeState,
          createdAt: x.timestamps.createdAt,
          collectionName: collectionNameByID.get(x.reportDescriptionID) ?? '',
        }))
      const storeIDs = new Set(storeItems.map((x) => x.id))
      const pendingItems = this.pendingReportDescriptionItems
        .filter((x) => idSet.has(x.reportDescriptionID) && !storeIDs.has(x.id))
        .map((x) => ({
          id: x.id,
          title: { [x.locale]: x.text },
          activeState: 'active' as const,
          createdAt: 0,
          collectionName: collectionNameByID.get(x.reportDescriptionID) ?? '',
        }))
      return [...storeItems, ...pendingItems].sort(
        (a, b) => a.createdAt - b.createdAt
      )
    },

    unitText(): string {
      const unitID =
        this.config.type === 'value-based' ||
        this.config.type === 'multi-select-number'
          ? this.config.unitID
          : ''
      if (!unitID) return ''
      const list = this.systemLists.find((l) => l.systemKey === 'result-units')
      const item = list?.items[unitID]
      if (!item) {
        return this.pendingUnits.find((x) => x.id === unitID)?.text ?? ''
      }
      return (
        item.text[this.locale] ??
        Object.values(item.text).find((v) => v !== '') ??
        ''
      )
    },

    // Multi-select text
    multiSelectTextSelectedIDs(): string[] {
      return this.modelValue?.type === 'multi-select-text'
        ? this.modelValue.items.map((x) => x.reportDescriptionItemID)
        : []
    },

    multiSelectTextNothingToAdd(): boolean {
      return this.modelValue?.type === 'multi-select-text'
        ? (this.modelValue.nothingToAdd ?? false)
        : false
    },

    // The rows as the drag list sees them: the item plus its key.
    multiSelectTextRows(): TMultiSelectTextRow[] {
      return this.multiSelectTextItems.map((item, index) => ({
        key: this.rowKeys[index] ?? `row-${index}`,
        item,
      }))
    },

    // Nothing to put in a different order with one row, and no dragging while the fields are locked
    // (no results permission, or the test is turned off).
    canReorderMultiSelectTextRows(): boolean {
      return !this.isDisabled && this.multiSelectTextItems.length > 1
    },

    // Multi-select number
    multiSelectNumberSelectedIDs(): string[] {
      return this.modelValue?.type === 'multi-select-number'
        ? this.modelValue.items.map((x) => x.reportDescriptionItemID)
        : []
    },

    multiSelectNumberNothingToAdd(): boolean {
      return this.modelValue?.type === 'multi-select-number'
        ? (this.modelValue.nothingToAdd ?? false)
        : false
    },

    multiSelectNumberSumValue(): string {
      return this.modelValue?.type === 'multi-select-number'
        ? (this.modelValue.sumValue ?? '')
        : ''
    },

    multiSelectNumberNumericSum(): number {
      if (this.modelValue?.type !== 'multi-select-number') return 0
      return sumNumericAnswers(this.modelValue.items)
    },

    multiSelectNumberSumHint(): string | null {
      if (this.modelValue?.type !== 'multi-select-number') return null
      const hasNumericAnswer = this.modelValue.items.some(
        (item) => item.value != null
      )
      if (!hasNumericAnswer) return null
      return this.unitText
        ? `${this.multiSelectNumberNumericSum} ${this.unitText}`
        : String(this.multiSelectNumberNumericSum)
    },
  },

  watch: {
    // The rows can also change from outside (the modal syncs from Firestore while it is open).
    multiSelectTextItems: {
      handler(): void {
        this.syncRowKeys()
      },
      immediate: true,
    },
  },

  methods: {
    // Answers offered for one row: the active ones plus the one that row has selected
    // (ui-display-conventions.md → Option Lists). Without the selected one, an answer
    // deactivated after it was chosen has no matching button or <option>, so the screen shows
    // nothing selected while the result still carries it. Once another answer is picked, the
    // inactive one drops out of the list.
    answersFor(selectedAnswerID: string | null | undefined) {
      return this.config.type === 'single-select' ||
        this.config.type === 'multi-select-text'
        ? this.config.answers.filter(
            (x) => x.activeState === 'active' || x.id === selectedAnswerID
          )
        : []
    },

    answerOverridesFor(
      selectedOverrideID: string | null | undefined
    ): { id: string; text: string }[] {
      if (this.config.type !== 'multi-select-number') return []
      return this.config.answerOverrides
        .filter(
          (x) => x.activeState === 'active' || x.id === selectedOverrideID
        )
        .map((x) => ({
          id: x.id,
          text: getTextObjValue(x.text, this.locale),
        }))
    },

    showsSuffixSelect(item: { suffixID?: string | null }): boolean {
      return this.suffixOptionsFor(item.suffixID).length > 0
    },

    // Pooled across every configured suffix Collection, the same way subject
    // items are pooled above.
    //
    // Per row, because the options are active items *plus the one that row has selected*
    // (ui-display-conventions.md → Option Lists). Without that, a suffix archived after it was
    // chosen has no matching <option>, so the dropdown shows "Intet suffiks" while the result
    // still carries it and the report still prints it — the screen would disagree with what is
    // saved. Once the user picks something else, the archived one drops out of the list.
    suffixOptionsFor(
      selectedSuffixID: string | null | undefined
    ): { value: string; text: string }[] {
      const idSet = new Set(this.suffixReportDescriptionIDs)
      const options = this.allReportDescriptionItems
        .filter(
          (x) =>
            idSet.has(x.reportDescriptionID) &&
            (x.activeState === 'active' || x.id === selectedSuffixID)
        )
        .map((x) => ({
          value: x.id,
          text: getTextObjValue(x.title, this.locale),
        }))
        .sort((a, b) => a.text.localeCompare(b.text))

      // A saved suffix whose Collection has since been taken off the test is still offered, and
      // marked, so the row can be cleared. Without it the dropdown is hidden and the suffix can
      // never be removed — while saving it unchanged is rejected, since the server only accepts
      // suffixes from the test's current Collections. Clearing is always accepted.
      if (
        selectedSuffixID &&
        !options.some((option) => option.value === selectedSuffixID)
      ) {
        const stale = this.allReportDescriptionItems.find(
          (x) => x.id === selectedSuffixID
        )
        if (stale) {
          options.push({
            value: stale.id,
            text: `${getTextObjValue(stale.title, this.locale)} ${this.$t('W: (ikke længere en mulighed)')}`,
          })
        }
      }

      return options
    },

    /** The shared ITextObj fallback chain, for use from the template. */
    answerLabel(text: ITextObj): string {
      return getTextObjValue(text, this.locale)
    },

    getReportDescriptionItemLabel(itemID: string): string {
      const item = this.pooledReportDescriptionItems.find(
        (x) => x.id === itemID
      )
      if (!item) return itemID
      return getTextObjValue(item.title, this.locale)
    },

    // Empty string when there is nothing to show — the template treats that as
    // "render no second line", so a single-Collection test is unchanged.
    getReportDescriptionItemSourceLabel(itemID: string): string {
      if (!this.showsCollectionSource) return ''
      return (
        this.pooledReportDescriptionItems.find((x) => x.id === itemID)
          ?.collectionName ?? ''
      )
    },

    // An item already on the result stays in the list: the same item can occur
    // on one sample more than once (with different suffixes), so picking it
    // again is valid. `isPicked` gives it the checkmark, and the second line
    // says how often it is picked in this test.
    reportDescriptionItemComboboxOptions(selectedIDs: string[]): {
      value: string
      text: string
      subtitle?: string
      isPicked?: boolean
    }[] {
      const pickCounts = new Map<string, number>()
      for (const id of selectedIDs) {
        pickCounts.set(id, (pickCounts.get(id) ?? 0) + 1)
      }
      const showSource = this.showsCollectionSource
      return this.pooledReportDescriptionItems
        .filter((item) => item.activeState === 'active')
        .map((item) => {
          const pickCount = pickCounts.get(item.id) ?? 0
          const subtitleParts: string[] = []
          if (pickCount > 0) subtitleParts.push(this.pickCountText(pickCount))
          if (showSource) subtitleParts.push(item.collectionName)
          return {
            value: item.id,
            text: getTextObjValue(item.title, this.locale),
            subtitle:
              subtitleParts.length > 0 ? subtitleParts.join(' · ') : undefined,
            isPicked: pickCount > 0,
          }
        })
        .sort((a, b) => a.text.localeCompare(b.text))
    },

    // Separate singular and plural strings: `W:` strings have no plural syntax.
    pickCountText(pickCount: number): string {
      return pickCount === 1
        ? this.$t('W: Valgt 1 gang')
        : this.$t('W: Valgt {count} gange', { count: pickCount })
    },

    getLimitText(side: 'min' | 'max'): string {
      if (this.config.type !== 'value-based') return ''
      const limit = side === 'min' ? this.config.minLimit : this.config.maxLimit
      if (limit == null) return ''
      const operator = side === 'min' ? '<' : '>'
      const formatted = new Intl.NumberFormat(this.locale).format(limit)
      return this.unitText
        ? `${operator} ${formatted} ${this.unitText}`
        : `${operator} ${formatted}`
    },

    setProved(value: boolean): void {
      this.$emit('update:modelValue', { type: 'prove-based', isProved: value })
    },

    onValueInput(event: Event): void {
      const raw = (event.target as HTMLInputElement).value
      const parsed = raw === '' ? null : parseFloat(raw)
      this.$emit('update:modelValue', {
        type: 'value-based',
        value: parsed !== null && isNaN(parsed) ? null : parsed,
        specialValue: null,
      })
    },

    onSpecialValueClick(value: TAnalysisResultSpecialValue): void {
      const next = this.specialValue === value ? null : value
      this.$emit('update:modelValue', {
        type: 'value-based',
        value: null,
        specialValue: next,
      })
    },

    selectAnswer(answerID: string): void {
      this.$emit('update:modelValue', { type: 'single-select', answerID })
    },

    emitMultiSelectText(
      patch: Partial<{
        items: {
          reportDescriptionItemID: string
          answerID: string | null
          suffixID?: string | null
        }[]
        nothingToAdd: boolean
      }>
    ): void {
      const current =
        this.modelValue?.type === 'multi-select-text' ? this.modelValue : null
      this.$emit('update:modelValue', {
        type: 'multi-select-text',
        reportDescriptionIDs: this.reportDescriptionIDs,
        nothingToAdd: current?.nothingToAdd ?? false,
        items: current?.items ?? [],
        ...patch,
      })
    },

    // Keeps `rowKeys` the same length as the rows: a row added or removed from outside gets keys
    // regenerated, which only costs the drag animation for that one change.
    syncRowKeys(): void {
      if (this.rowKeys.length === this.multiSelectTextItems.length) return
      this.rowKeys = this.multiSelectTextItems.map(() => nextRowKey())
    },

    onMultiSelectTextReorder(rows: TMultiSelectTextRow[]): void {
      this.rowKeys = rows.map((row) => row.key)
      this.emitMultiSelectText({ items: rows.map((row) => row.item) })
    },

    addMultiSelectTextItem(itemID: string): void {
      this.syncRowKeys()
      this.rowKeys = [...this.rowKeys, nextRowKey()]
      const current =
        this.modelValue?.type === 'multi-select-text'
          ? this.modelValue.items
          : []
      this.emitMultiSelectText({
        items: [
          ...current,
          { reportDescriptionItemID: itemID, answerID: null, suffixID: null },
        ],
        nothingToAdd: false,
      })
    },

    onMultiSelectTextComboboxSelect(itemID: string): void {
      if (!itemID) return
      this.addMultiSelectTextItem(itemID)
    },

    onMultiSelectTextPendingCreate(text: string): void {
      const id = generateFirestoreId()
      this.$emit('on-pending-report-description-item-create', {
        id,
        // Only reachable when exactly one Collection is pooled (allowCreate
        // is off otherwise — see canCreateReportDescriptionItemOnTheFly), so
        // this is always the one and only target.
        reportDescriptionID: this.reportDescriptionIDs[0] ?? '',
        text,
        locale: this.locale,
      })
      this.addMultiSelectTextItem(id)
    },

    removeMultiSelectTextItem(index: number): void {
      this.syncRowKeys()
      this.rowKeys = this.rowKeys.filter((_, i) => i !== index)
      this.emitMultiSelectText({
        items: this.multiSelectTextItems.filter((_, i) => i !== index),
      })
    },

    onMultiSelectTextAnswerChange(index: number, event: Event): void {
      const answerID = (event.target as HTMLSelectElement).value || null
      this.emitMultiSelectText({
        items: this.multiSelectTextItems.map((x, i) =>
          i === index ? { ...x, answerID } : x
        ),
      })
    },

    onMultiSelectTextSuffixChange(index: number, event: Event): void {
      const suffixID = (event.target as HTMLSelectElement).value || null
      this.emitMultiSelectText({
        items: this.multiSelectTextItems.map((x, i) =>
          i === index ? { ...x, suffixID } : x
        ),
      })
    },

    toggleMultiSelectTextNothingToAdd(): void {
      this.emitMultiSelectText({
        nothingToAdd: !this.multiSelectTextNothingToAdd,
      })
    },

    emitMultiSelectNumber(
      patch: Partial<{
        items: {
          reportDescriptionItemID: string
          value: number | null
          answerOverrideID: string | null
          suffixID?: string | null
        }[]
        nothingToAdd: boolean
        sumValue: string | null
      }>
    ): void {
      const current =
        this.modelValue?.type === 'multi-select-number' ? this.modelValue : null
      this.$emit('update:modelValue', {
        type: 'multi-select-number',
        reportDescriptionIDs: this.reportDescriptionIDs,
        nothingToAdd: current?.nothingToAdd ?? false,
        items: current?.items ?? [],
        sumValue: current?.sumValue ?? null,
        ...patch,
      })
    },

    addMultiSelectNumberItem(itemID: string): void {
      const current =
        this.modelValue?.type === 'multi-select-number'
          ? this.modelValue.items
          : []
      this.emitMultiSelectNumber({
        items: [
          ...current,
          {
            reportDescriptionItemID: itemID,
            value: null,
            answerOverrideID: null,
            suffixID: null,
          },
        ],
        nothingToAdd: false,
      })
    },

    onMultiSelectNumberComboboxSelect(itemID: string): void {
      if (!itemID) return
      this.addMultiSelectNumberItem(itemID)
    },

    onMultiSelectNumberPendingCreate(text: string): void {
      const id = generateFirestoreId()
      this.$emit('on-pending-report-description-item-create', {
        id,
        // See the matching comment in onMultiSelectTextPendingCreate above.
        reportDescriptionID: this.reportDescriptionIDs[0] ?? '',
        text,
        locale: this.locale,
      })
      this.addMultiSelectNumberItem(id)
    },

    removeMultiSelectNumberItem(index: number): void {
      const remaining = this.multiSelectNumberItems.filter(
        (_, i) => i !== index
      )
      if (remaining.length === 0) {
        this.emitMultiSelectNumber({ items: remaining, sumValue: '' })
        return
      }
      this.emitMultiSelectNumber({
        items: remaining,
        ...this.autoSumPatch(remaining),
      })
    },

    onMultiSelectNumberValueInput(index: number, event: Event): void {
      const raw = (event.target as HTMLInputElement).value
      const updated = this.multiSelectNumberItems.map((x, i) =>
        i === index ? { ...x, value: raw === '' ? null : parseFloat(raw) } : x
      )
      this.emitMultiSelectNumber({
        items: updated,
        ...this.autoSumPatch(updated),
      })
    },

    onMultiSelectNumberSuffixChange(index: number, event: Event): void {
      const suffixID = (event.target as HTMLSelectElement).value || null
      this.emitMultiSelectNumber({
        items: this.multiSelectNumberItems.map((x, i) =>
          i === index ? { ...x, suffixID } : x
        ),
      })
    },

    onMultiSelectNumberOverrideChange(index: number, event: Event): void {
      const overrideID = (event.target as HTMLSelectElement).value || null
      const updated = this.multiSelectNumberItems.map((x, i) =>
        i === index
          ? {
              ...x,
              answerOverrideID: overrideID,
              value: overrideID !== null ? null : x.value,
            }
          : x
      )
      this.emitMultiSelectNumber({
        items: updated,
        ...this.autoSumPatch(updated),
      })
    },

    onMultiSelectNumberSumInput(event: Event): void {
      this.emitMultiSelectNumber({
        sumValue: (event.target as HTMLInputElement).value,
      })
    },

    toggleMultiSelectNumberNothingToAdd(): void {
      this.emitMultiSelectNumber({
        nothingToAdd: !this.multiSelectNumberNothingToAdd,
      })
    },

    // What the Sum field becomes when the rows change.
    //  - Empty, or exactly what the old rows produced: it follows the rows.
    //  - Edited by hand ("ca. 15 CFU/m³ (estimat)"): the old sum is looked up as a whole
    //    number inside the text and only that number is swapped for the new sum, so the
    //    words around it survive. If the old sum is not there exactly once, the text is
    //    left alone; emptying the field hands it back to the automatic sum.
    autoSumPatch(updatedItems: {
      reportDescriptionItemID: string
      value: number | null
      answerOverrideID: string | null
    }[]): { sumValue?: string } {
      // The Sum field only exists when the test has the sum option on. Without it
      // nothing here may write a sum: the report would print one the lab never saw.
      if (this.config.type !== 'multi-select-number' || !this.config.hasSumValue) {
        return {}
      }
      const currentSum = this.multiSelectNumberSumValue
      const previousItems = this.multiSelectNumberItems
      if (
        currentSum === '' ||
        currentSum === this.computeAutoSumValue(previousItems)
      ) {
        return { sumValue: this.computeAutoSumValue(updatedItems) }
      }
      const hasNumber = (items: { value: number | null }[]) =>
        items.some((item) => item.value != null)
      if (!hasNumber(previousItems) || !hasNumber(updatedItems)) return {}
      const swapped = replaceNumberInSum(
        currentSum,
        sumNumericAnswers(previousItems),
        sumNumericAnswers(updatedItems)
      )
      return swapped === null ? {} : { sumValue: swapped }
    },

    // The sum of every row that holds a number. Rows with an override answer
    // (e.g. "Overgroet") are left out, whether or not one is present elsewhere.
    computeAutoSumValue(
      items: {
        reportDescriptionItemID: string
        value: number | null
        answerOverrideID: string | null
      }[]
    ): string {
      const hasNumericAnswer = items.some((item) => item.value != null)
      if (!hasNumericAnswer) return ''
      const sum = sumNumericAnswers(items)
      return this.unitText ? `${sum} ${this.unitText}` : String(sum)
    },
  },
}
</script>
